/**
 * API Documentation Page
 *
 * Comprehensive API documentation for developers.
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PublicFooter } from '../../components/layout/PublicFooter';

interface Endpoint {
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  path: string;
  description: string;
  auth: boolean;
  scope?: string;
  queryParams?: string;
  requestBody?: string;
  responseBody: string;
  example?: string;
}

const endpoints: Record<string, Endpoint[]> = {
  'API Keys': [
    {
      method: 'GET',
      path: '/api/api-keys',
      description: 'List all your API keys. Returns key metadata (prefix, scopes, expiration) but never the full key.',
      auth: true,
      responseBody: `{
  "apiKeys": [
    {
      "id": "uuid",
      "name": "Production Key",
      "keyPrefix": "fk_abc123...",
      "scopes": ["forms:read", "forms:write", "submissions:read"],
      "lastUsedAt": "2026-01-15T08:30:00Z",
      "expiresAt": null,
      "isExpired": false,
      "createdAt": "2026-01-01T00:00:00Z"
    }
  ]
}`,
      example: 'You must be logged into the web dashboard to manage API keys. API keys cannot be managed via the API itself.',
    },
    {
      method: 'POST',
      path: '/api/api-keys',
      description: 'Create a new API key for programmatic access',
      auth: true,
      requestBody: `{
  "name": "Production Key",
  "scopes": ["forms:read", "forms:write", "submissions:read"],
  "expiresIn": 365
}`,
      responseBody: `{
  "success": true,
  "apiKey": {
    "id": "uuid",
    "name": "Production Key",
    "key": "fk_abc123def456...",
    "keyPrefix": "fk_abc123...",
    "scopes": ["forms:read", "forms:write", "submissions:read"],
    "expiresAt": "2027-01-01T00:00:00Z",
    "createdAt": "2026-01-01T00:00:00Z"
  },
  "warning": "Save this API key now. It will not be shown again."
}`,
      example: 'The full API key is only shown once upon creation. Store it securely. Available scopes: forms:read, forms:write, submissions:read, submissions:write. Default scopes (if none specified): forms:read, forms:write, submissions:read. Maximum 10 active API keys per account.',
    },
    {
      method: 'DELETE',
      path: '/api/api-keys/:id',
      description: 'Revoke an API key permanently. This action cannot be undone.',
      auth: true,
      responseBody: `{
  "success": true
}`,
    },
  ],
  'Forms': [
    {
      method: 'GET',
      path: '/api/forms',
      description: 'List all forms, quizzes, and polls you own. Supports filtering by status and search.',
      auth: true,
      scope: 'forms:read',
      queryParams: `status   Filter by status: all (default), draft, published
search   Search by form name, slug, or description
page     Page number (default: 1)
limit    Results per page (default: 20, max: 100)`,
      responseBody: `{
  "forms": [
    {
      "id": "7aeeeaa5-be0f-4b4e-8f81-5ed93d11f53d",
      "name": "Contact Form",
      "slug": "contact-form-a1b2c3",
      "description": "Main contact form",
      "status": "published",
      "form_type": "form",
      "version": 3,
      "created_at": "2026-01-01T12:00:00Z",
      "updated_at": "2026-01-15T08:30:00Z"
    }
  ],
  "pagination": {
    "page": 1,
    "limit": 20,
    "total": 12,
    "totalPages": 1
  }
}`,
      example: 'Returns all forms including regular forms, quizzes, and polls. Use the status parameter to filter.',
    },
    {
      method: 'POST',
      path: '/api/forms',
      description: 'Create a new form. New forms start as drafts with an empty field list. Use the Update endpoint to add fields, then Publish when ready.',
      auth: true,
      scope: 'forms:write',
      requestBody: `{
  "name": "Customer Feedback",
  "description": "Collect customer feedback"
}`,
      responseBody: `{
  "id": "a1b2c3d4-e5f6-7890-abcd-ef1234567890",
  "slug": "customer-feedback-x7k9m2",
  "name": "Customer Feedback",
  "form_type": "form",
  "ownerId": "uuid",
  "role": "owner"
}`,
      example: 'Set formType to "form", "quiz", or "poll". Configure quiz/poll settings after creation via the Update endpoint.',
    },
    {
      method: 'GET',
      path: '/api/forms/:id',
      description: 'Get a specific form by ID, including its full schema, settings, and metadata.',
      auth: true,
      scope: 'forms:read',
      responseBody: `{
  "id": "uuid",
  "name": "Customer Feedback",
  "slug": "customer-feedback-x7k9m2",
  "description": "Collect customer feedback",
  "status": "published",
  "schema": {
    "version": 2,
    "fields": [
      {
        "id": "field_1",
        "name": "full_name",
        "type": "text",
        "label": "Full Name",
        "required": true
      },
      {
        "id": "field_2",
        "name": "email",
        "type": "email",
        "label": "Email Address",
        "required": true
      }
    ],
    "steps": [],
    "settings": {
      "submitButtonText": "Submit",
      "successMessage": "Thank you!",
      "theme": "light",
      "submitActions": [{ "type": "database" }]
    },
    "conditionalLogic": []
  },
  "settings": {
    "submitButtonText": "Submit",
    "successMessage": "Thank you!"
  },
  "form_type": "form",
  "version": 2,
  "created_at": "2026-01-01T10:00:00Z",
  "updated_at": "2026-01-15T08:30:00Z",
  "published_at": "2026-01-02T12:00:00Z"
}`,
    },
    {
      method: 'PUT',
      path: '/api/forms/:id',
      description: 'Update a form. When the schema is included, the form version is incremented and the previous version is saved to history.',
      auth: true,
      scope: 'forms:write',
      requestBody: `{
  "name": "Updated Contact Form",
  "schema": {
    "version": 1,
    "fields": [
      {
        "id": "f1",
        "name": "name",
        "type": "text",
        "label": "Full Name",
        "required": true,
        "placeholder": "Enter your name"
      },
      {
        "id": "f2",
        "name": "email",
        "type": "email",
        "label": "Email",
        "required": true
      },
      {
        "id": "f3",
        "name": "message",
        "type": "textarea",
        "label": "Message",
        "required": false
      }
    ],
    "steps": [],
    "settings": {
      "submitButtonText": "Send",
      "successMessage": "Thanks!",
      "theme": "light",
      "submitActions": [{ "type": "database" }]
    },
    "conditionalLogic": []
  },
  "changeSummary": "Added name, email, and message fields"
}`,
      responseBody: `{
  "success": true,
  "version": 2
}`,
      example: 'All fields are optional. Include changeSummary when updating the schema to keep a version history note.',
    },
    {
      method: 'DELETE',
      path: '/api/forms/:id',
      description: 'Permanently delete a form and all its submissions, file uploads, and permissions.',
      auth: true,
      scope: 'forms:write',
      responseBody: `{
  "success": true
}`,
    },
    {
      method: 'POST',
      path: '/api/forms/:id/publish',
      description: 'Publish a draft form, making it publicly accessible at its unique URL. The form must have at least one field.',
      auth: true,
      scope: 'forms:write',
      responseBody: `{
  "success": true,
  "slug": "customer-feedback-x7k9m2",
  "form_type": "form",
  "publicUrl": "/f/customer-feedback-x7k9m2",
  "resultsUrl": "/results/aBcDeFgHjKmNpQrStUvWxYz23456789ab",
  "resultsKey": "aBcDeFgHjKmNpQrStUvWxYz23456789ab"
}`,
      example: 'The full public URL is https://xt4m.com/f/your-form-slug. URL prefixes vary by form type: /f/ (form), /p/ (poll), /q/ (quiz), /l/ (live quiz).',
    },
  ],
  'Submissions': [
    {
      method: 'GET',
      path: '/api/forms/:id/submissions',
      description: 'List all submissions for a form with pagination and status filtering.',
      auth: true,
      scope: 'submissions:read',
      queryParams: `status   Filter: all (default), new, reviewed, spam
page     Page number (default: 1)
limit    Results per page (default: 20, max: 100)`,
      responseBody: `{
  "submissions": [
    {
      "id": "sub-1234-5678-abcd",
      "form_id": "uuid",
      "form_version": 2,
      "data": {
        "full_name": "John Doe",
        "email": "john@example.com",
        "message": "Great service!"
      },
      "metadata": {
        "ip": "203.0.113.42",
        "userAgent": "Mozilla/5.0...",
        "referrer": "https://example.com",
        "submittedAt": "2026-02-01T14:30:00Z"
      },
      "status": "new",
      "created_at": "2026-02-01T14:30:00Z",
      "is_encrypted": false
    }
  ],
  "pagination": {
    "page": 1,
    "limit": 20,
    "total": 100,
    "totalPages": 5
  }
}`,
    },
    {
      method: 'GET',
      path: '/api/submissions',
      description: 'List submissions across all your forms in a single request. Useful for dashboard views.',
      auth: true,
      scope: 'submissions:read',
      queryParams: `form_id  Filter to a specific form (optional)
status   Filter: all (default), new, reviewed, spam
page     Page number (default: 1)
limit    Results per page (default: 20, max: 100)`,
      responseBody: `{
  "submissions": [
    {
      "id": "sub-1234-5678-abcd",
      "form_id": "uuid",
      "form_name": "Contact Form",
      "form_version": 2,
      "data": { "full_name": "John Doe", "email": "john@example.com" },
      "metadata": { "ip": "203.0.113.42", "submittedAt": "2026-02-01T14:30:00Z" },
      "status": "new",
      "created_at": "2026-02-01T14:30:00Z",
      "is_encrypted": false
    }
  ],
  "pagination": { "page": 1, "limit": 20, "total": 250, "totalPages": 13 }
}`,
      example: 'Returns submissions across all forms you own. Each submission includes the form_name for context. Use form_id parameter to filter to a single form.',
    },
    {
      method: 'POST',
      path: '/api/forms/:id/submissions',
      description: 'Submit a form response. This endpoint is publicly accessible without authentication for published forms.',
      auth: false,
      scope: 'submissions:write (or no auth for published forms)',
      requestBody: `{
  "data": {
    "full_name": "Jane Smith",
    "email": "jane@example.com",
    "message": "Looking forward to hearing from you"
  },
  "uploadIds": ["upload-id-1"],
  "turnstileToken": "captcha-token-if-enabled"
}`,
      responseBody: `{
  "success": true,
  "submissionId": "sub-9876-5432-wxyz",
  "message": "Thank you! We will get back to you soon."
}`,
      example: 'The data keys must match the field name values in the form schema. For file fields, upload files first via /api/upload/presign, then include the uploadId. The turnstileToken field is required only if CAPTCHA is enabled on the form.',
    },
    {
      method: 'GET',
      path: '/api/forms/:id/submissions/:submissionId',
      description: 'Get a specific submission by ID.',
      auth: true,
      scope: 'submissions:read',
      responseBody: `{
  "id": "sub-1234-5678-abcd",
  "form_id": "uuid",
  "form_version": 2,
  "data": {
    "full_name": "John Doe",
    "email": "john@example.com",
    "message": "Great service!"
  },
  "metadata": {
    "ip": "203.0.113.42",
    "userAgent": "Mozilla/5.0...",
    "submittedAt": "2026-02-01T14:30:00Z"
  },
  "status": "new",
  "created_at": "2026-02-01T14:30:00Z"
}`,
    },
    {
      method: 'GET',
      path: '/api/forms/:id/submissions/export',
      description: 'Export all submissions as JSON or CSV.',
      auth: true,
      scope: 'submissions:read',
      queryParams: `format   json (default) or csv
status   Filter: all (default), new, reviewed, spam`,
      responseBody: `{
  "form": {
    "id": "uuid",
    "name": "Customer Feedback"
  },
  "fields": [
    { "name": "full_name", "label": "Full Name" },
    { "name": "email", "label": "Email" },
    { "name": "message", "label": "Message" }
  ],
  "submissions": [
    {
      "id": "sub-9876-5432-wxyz",
      "formVersion": 2,
      "data": { "full_name": "Jane Smith", "email": "jane@example.com" },
      "metadata": { "submittedAt": "2026-02-01T15:00:00Z" },
      "status": "new",
      "createdAt": "2026-02-01T15:00:00Z"
    }
  ],
  "exportedAt": "2026-02-03T10:00:00Z"
}`,
      example: 'CSV format returns a file download with columns: ID, Created At, Status, followed by each form field. Use format=csv for spreadsheet-compatible exports.',
    },
    {
      method: 'DELETE',
      path: '/api/forms/:id/submissions/:submissionId',
      description: 'Delete a submission permanently.',
      auth: true,
      scope: 'submissions:write',
      responseBody: `{
  "success": true
}`,
    },
  ],
  'Public Forms': [
    {
      method: 'GET',
      path: '/api/f/:slug',
      description: 'Get a published form by its slug. No authentication required. This is the endpoint used by xtFORM to render forms and can be used to build custom renderers.',
      auth: false,
      responseBody: `{
  "id": "uuid",
  "name": "Customer Feedback",
  "slug": "customer-feedback-x7k9m2",
  "description": "Collect customer feedback",
  "schema": {
    "version": 2,
    "fields": [
      { "id": "f1", "name": "full_name", "type": "text", "label": "Full Name", "required": true },
      { "id": "f2", "name": "email", "type": "email", "label": "Email", "required": true }
    ],
    "steps": [],
    "settings": {
      "submitButtonText": "Submit",
      "successMessage": "Thank you!",
      "theme": "light"
    },
    "conditionalLogic": []
  },
  "form_type": "form",
  "version": 2
}`,
      example: 'URL patterns by form type: GET /api/f/:slug (form), GET /api/p/:slug (poll), GET /api/q/:slug (quiz), GET /api/l/:slug (live quiz). The public form page URLs follow the same pattern: https://xt4m.com/f/:slug, /p/:slug, /q/:slug, /l/:slug.',
    },
  ],
  'Live Quizzes': [
    {
      method: 'POST',
      path: '/api/live-quiz/sessions',
      description: 'Create a new live quiz session with a 6-digit game PIN for players to join.',
      auth: true,
      requestBody: `{
  "formId": "uuid",
  "settings": {
    "questionTimeSeconds": 20,
    "showQuestionOnPlayerDevice": true,
    "enableStreakBonus": true,
    "maxPointsPerQuestion": 1000
  }
}`,
      responseBody: `{
  "sessionId": "uuid",
  "gamePin": "123456",
  "websocketUrl": "wss://xt4m.com/api/live-quiz/ws/uuid",
  "totalQuestions": 10
}`,
      example: 'Form must be configured for live quiz mode. The 6-digit game PIN is shared with players who join at the live quiz URL.',
    },
    {
      method: 'GET',
      path: '/api/live-quiz/sessions',
      description: 'List all quiz sessions you have hosted.',
      auth: true,
      responseBody: `{
  "sessions": [
    {
      "id": "uuid",
      "formId": "uuid",
      "formName": "General Knowledge Quiz",
      "gamePin": "123456",
      "status": "ended",
      "participantCount": 25,
      "createdAt": "2026-01-01T00:00:00Z",
      "startedAt": "2026-01-01T00:05:00Z",
      "endedAt": "2026-01-01T00:15:00Z"
    }
  ],
  "total": 1,
  "limit": 20,
  "offset": 0
}`,
    },
    {
      method: 'GET',
      path: '/api/live-quiz/:sessionId/results',
      description: 'Get the final leaderboard and per-question statistics for a completed quiz session.',
      auth: true,
      responseBody: `{
  "sessionId": "uuid",
  "status": "ended",
  "leaderboard": [
    {
      "rank": 1,
      "playerName": "Alice",
      "score": 9500,
      "correctAnswers": 10,
      "totalQuestions": 10
    }
  ],
  "questionStats": [
    {
      "questionIndex": 0,
      "correctCount": 20,
      "incorrectCount": 5,
      "avgResponseTime": 3.2
    }
  ]
}`,
    },
  ],
  'Polls': [
    {
      method: 'GET',
      path: '/api/forms/:id/poll',
      description: 'Get poll settings for a form configured as a poll.',
      auth: true,
      responseBody: `{
  "isPoll": true,
  "settings": {
    "pollType": "single",
    "showResultsAfterVote": true,
    "anonymousVoting": true,
    "votingDeadline": "2026-12-31T23:59:59Z",
    "resultsVisibility": "after_vote"
  }
}`,
    },
    {
      method: 'PUT',
      path: '/api/forms/:id/poll',
      description: 'Update poll settings or enable poll mode on a form.',
      auth: true,
      requestBody: `{
  "pollType": "single",
  "showResultsAfterVote": true,
  "anonymousVoting": true,
  "votingDeadline": "2026-12-31T23:59:59Z",
  "resultsVisibility": "after_vote"
}`,
      responseBody: `{
  "success": true,
  "isPoll": true
}`,
      example: 'Poll types: single, multiple, ranked, rating. Results visibility options: always, after_vote, after_close, owner_only.',
    },
    {
      method: 'GET',
      path: '/api/forms/:id/poll-results',
      description: 'Get live poll voting results with vote counts and percentages.',
      auth: true,
      responseBody: `{
  "canViewResults": true,
  "results": [
    {
      "optionId": "opt1",
      "optionLabel": "Option A",
      "voteCount": 45,
      "percentage": 60.0,
      "rankScore": null
    },
    {
      "optionId": "opt2",
      "optionLabel": "Option B",
      "voteCount": 30,
      "percentage": 40.0,
      "rankScore": null
    }
  ],
  "totalVotes": 75,
  "pollType": "single",
  "isClosed": false
}`,
      example: 'Results visibility depends on poll settings. Some polls require voting before viewing results.',
    },
  ],
  'Analytics': [
    {
      method: 'GET',
      path: '/api/forms/:id/analytics',
      description: 'Get detailed analytics for a form including conversion funnel, field-level insights, and device breakdown.',
      auth: true,
      queryParams: `period   Time window: 7d, 30d (default), 90d`,
      responseBody: `{
  "period": "30d",
  "totals": {
    "views": 1250,
    "starts": 890,
    "completions": 645,
    "dropOffs": 245,
    "conversionRate": 72.5,
    "dropOffRate": 27.5,
    "avgCompletionTime": 185
  },
  "daily": [
    {
      "date": "2026-01-01",
      "views": 50,
      "starts": 35,
      "completions": 28,
      "avgCompletionTimeSeconds": 180,
      "dropOffs": 7
    }
  ],
  "fieldAnalytics": [
    {
      "fieldName": "Email",
      "interactionCount": 890,
      "avgTimeMs": 3500,
      "errorCount": 45
    }
  ],
  "deviceBreakdown": [
    { "deviceType": "desktop", "count": 450 },
    { "deviceType": "mobile", "count": 350 },
    { "deviceType": "tablet", "count": 90 }
  ]
}`,
      example: 'Available periods: 7d, 30d, 90d. Shows conversion funnel, field-level insights, and device analytics.',
    },
  ],
  'Webhooks': [
    {
      method: 'POST',
      path: '/api/forms/:id/test-webhook',
      description: 'Send a test payload to verify your webhook URL is reachable and properly configured.',
      auth: true,
      requestBody: `{
  "webhook": {
    "url": "https://your-server.com/xtform-webhook",
    "method": "POST"
  }
}`,
      responseBody: `{
  "success": true,
  "statusCode": 200,
  "responseTime": 245
}`,
      example: 'Webhooks are configured in the form settings under submitActions. When a submission is received, xtFORM sends a POST request to your webhook URL containing: formId, submissionId, data, and metadata. For signed webhooks, verify the X-Webhook-Signature header using HMAC-SHA256.',
    },
  ],
};

const methodColors: Record<string, string> = {
  GET: 'bg-green-100 text-green-700',
  POST: 'bg-blue-100 text-blue-700',
  PUT: 'bg-yellow-100 text-yellow-700',
  PATCH: 'bg-orange-100 text-orange-700',
  DELETE: 'bg-red-100 text-red-700',
};

export default function ApiDocsPage() {
  const [activeSection, setActiveSection] = useState<string>('API Keys');
  const [expandedEndpoint, setExpandedEndpoint] = useState<string | null>(null);
  const { i18n } = useTranslation();

  const sections = Object.keys(endpoints);

  // Force English language and LTR direction for API docs
  useEffect(() => {
    const originalLang = i18n.language;
    i18n.changeLanguage('en');
    document.documentElement.setAttribute('dir', 'ltr');

    return () => {
      // Restore user's language and direction on unmount
      i18n.changeLanguage(originalLang);
      document.documentElement.setAttribute('dir', i18n.dir());
    };
  }, [i18n]);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-4">
              <Link to="/" className="flex items-center gap-2">
                <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span className="text-xl font-bold text-gray-900">xtFORM</span>
              </Link>
              <span className="text-gray-300">|</span>
              <span className="text-gray-600 font-medium">API Documentation</span>
            </div>
            <div className="flex items-center gap-4">
              <Link to="/login" className="text-gray-600 hover:text-gray-900 text-sm font-medium">
                Sign In
              </Link>
              <Link
                to="/register"
                className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium text-sm hover:bg-blue-700 transition-colors"
              >
                Get Started
              </Link>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex gap-8">
          {/* Sidebar */}
          <aside className="hidden lg:block w-64 flex-shrink-0">
            <nav className="sticky top-24 space-y-1">
              <h3 className="px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                Endpoints
              </h3>
              {sections.map((section) => (
                <button
                  key={section}
                  onClick={() => setActiveSection(section)}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    activeSection === section
                      ? 'bg-blue-50 text-blue-700'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  {section}
                </button>
              ))}

              <div className="pt-6">
                <h3 className="px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                  Resources
                </h3>
                <Link
                  to="/settings/api-keys"
                  className="block px-3 py-2 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-100"
                >
                  Manage API Keys
                </Link>
              </div>
            </nav>
          </aside>

          {/* Main Content */}
          <main className="flex-1 min-w-0">
            {/* Introduction */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 mb-8">
              <h1 className="text-3xl font-bold text-gray-900 mb-4">API Reference</h1>
              <p className="text-gray-600 mb-6">
                Welcome to the xtFORM API documentation. Our RESTful API allows you to programmatically
                create and manage forms, quizzes, and polls, handle submissions, retrieve analytics,
                configure webhooks, and integrate form functionality into your applications.
              </p>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <h3 className="font-semibold text-blue-900 mb-2">Quick Start</h3>
                <ol className="text-blue-800 text-sm space-y-1 list-decimal list-inside">
                  <li>Sign up for a free account at xtFORM</li>
                  <li>Log in and go to <strong>Settings &rarr; API Keys</strong></li>
                  <li>Create an API key with the scopes you need</li>
                  <li>Include the key in the <code className="bg-blue-100 px-1 rounded">Authorization</code> header</li>
                </ol>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-2">Base URL</h3>
                  <code className="text-sm bg-gray-200 px-2 py-1 rounded">
                    https://xt4m.com/api
                  </code>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-2">Authentication</h3>
                  <code className="text-sm bg-gray-200 px-2 py-1 rounded">
                    Authorization: Bearer fk_your_api_key
                  </code>
                </div>
              </div>
            </div>

            {/* Authentication Section */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Authentication</h2>
              <p className="text-gray-600 mb-4">
                The API uses API Keys for authentication. Generate a key from the dashboard — it is shown only once and uses the
                format <code className="bg-gray-100 px-1 rounded text-sm">fk_</code> followed by 64 hex characters.
              </p>
              <div className="space-y-4">
                <div className="bg-blue-50 rounded-lg p-4">
                  <h4 className="font-semibold text-blue-900 mb-2">API Key Scopes</h4>
                  <p className="text-blue-800 text-sm mb-3">
                    When creating an API key, select the scopes your integration needs:
                  </p>
                  <table className="w-full text-sm mb-3">
                    <tbody className="text-blue-800">
                      <tr className="border-b border-blue-200">
                        <td className="py-1.5 font-mono">forms:read</td>
                        <td className="py-1.5">List and read form definitions</td>
                      </tr>
                      <tr className="border-b border-blue-200">
                        <td className="py-1.5 font-mono">forms:write</td>
                        <td className="py-1.5">Create, update, delete, and publish forms</td>
                      </tr>
                      <tr className="border-b border-blue-200">
                        <td className="py-1.5 font-mono">submissions:read</td>
                        <td className="py-1.5">Read and export form submissions</td>
                      </tr>
                      <tr>
                        <td className="py-1.5 font-mono">submissions:write</td>
                        <td className="py-1.5">Submit data to forms</td>
                      </tr>
                    </tbody>
                  </table>
                  <p className="text-blue-700 text-xs">
                    Default scopes (if none specified): forms:read, forms:write, submissions:read
                  </p>
                </div>
                <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
                  <h4 className="font-semibold text-amber-900 mb-2">Security Best Practices</h4>
                  <ul className="text-amber-800 text-sm space-y-1 list-disc list-inside">
                    <li>Never expose API keys in client-side code served to browsers</li>
                    <li>Always route API requests through your backend server</li>
                    <li>Use different keys for different applications or environments</li>
                    <li>Set appropriate scopes to limit access</li>
                    <li>Set expiration dates for temporary integrations</li>
                    <li>Maximum 10 active API keys per account</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Scope & Limitations */}
            <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl border border-purple-200 p-8 mb-8">
              <h2 className="text-xl font-bold text-purple-900 mb-4">API Scope & Limitations</h2>
              <p className="text-purple-800 mb-4">
                This API provides access to core form, quiz, and poll functionality. The following features are
                <strong> NOT available</strong> via the API and must be accessed through the web dashboard:
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                <ul className="text-purple-800 text-sm space-y-2 list-disc list-inside">
                  <li>User account management (registration, login, profile)</li>
                  <li>Team & workspace management</li>
                  <li>Billing & subscription management</li>
                  <li>System administration functions</li>
                </ul>
                <ul className="text-purple-800 text-sm space-y-2 list-disc list-inside">
                  <li>Email template customization</li>
                  <li>OAuth provider configuration</li>
                  <li>Domain & branding settings</li>
                  <li>Audit logs & security settings</li>
                </ul>
              </div>
              <div className="mt-4 bg-white/50 rounded-lg p-3 border border-purple-300">
                <p className="text-purple-900 text-sm">
                  <strong>Note:</strong> All API access is scoped to the user who created the API key.
                  You can only access forms, quizzes, and polls that you own or have been granted access to.
                </p>
              </div>
            </div>

            {/* Mobile Section Selector */}
            <div className="lg:hidden mb-6">
              <select
                value={activeSection}
                onChange={(e) => setActiveSection(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {sections.map((section) => (
                  <option key={section} value={section}>
                    {section}
                  </option>
                ))}
              </select>
            </div>

            {/* Endpoints */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-8 py-6 border-b border-gray-200">
                <h2 className="text-xl font-bold text-gray-900">{activeSection}</h2>
              </div>

              <div className="divide-y divide-gray-200">
                {endpoints[activeSection]?.map((endpoint, index) => {
                  const endpointKey = `${endpoint.method}-${endpoint.path}`;
                  const isExpanded = expandedEndpoint === endpointKey;

                  return (
                    <div key={index} className="hover:bg-gray-50">
                      <button
                        onClick={() => setExpandedEndpoint(isExpanded ? null : endpointKey)}
                        className="w-full px-8 py-4 flex items-center gap-4 text-left"
                      >
                        <span className={`px-2 py-1 rounded text-xs font-bold ${methodColors[endpoint.method]}`}>
                          {endpoint.method}
                        </span>
                        <code className="text-sm font-mono text-gray-700 flex-1">
                          {endpoint.path}
                        </code>
                        {endpoint.auth ? (
                          <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">
                            Auth Required
                          </span>
                        ) : (
                          <span className="px-2 py-0.5 bg-green-50 text-green-600 rounded text-xs">
                            Public
                          </span>
                        )}
                        <svg
                          className={`w-5 h-5 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>

                      {isExpanded && (
                        <div className="px-8 pb-6 border-t border-gray-100 bg-gray-50">
                          <p className="text-gray-600 py-4">{endpoint.description}</p>

                          {endpoint.scope && (
                            <div className="mb-4 flex items-center gap-2">
                              <span className="text-xs font-semibold text-gray-500 uppercase">Scope:</span>
                              <code className="text-xs bg-purple-50 text-purple-700 px-2 py-0.5 rounded">{endpoint.scope}</code>
                            </div>
                          )}

                          {endpoint.queryParams && (
                            <div className="mb-4">
                              <h4 className="text-sm font-semibold text-gray-700 mb-2">Query Parameters</h4>
                              <pre className="bg-gray-900 text-gray-100 rounded-lg p-4 overflow-x-auto text-sm">
                                {endpoint.queryParams}
                              </pre>
                            </div>
                          )}

                          {endpoint.requestBody && (
                            <div className="mb-4">
                              <h4 className="text-sm font-semibold text-gray-700 mb-2">Request Body</h4>
                              <pre className="bg-gray-900 text-gray-100 rounded-lg p-4 overflow-x-auto text-sm">
                                {endpoint.requestBody}
                              </pre>
                            </div>
                          )}

                          <div className="mb-4">
                            <h4 className="text-sm font-semibold text-gray-700 mb-2">Response</h4>
                            <pre className="bg-gray-900 text-gray-100 rounded-lg p-4 overflow-x-auto text-sm">
                              {endpoint.responseBody}
                            </pre>
                          </div>

                          {endpoint.example && (
                            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800">
                              <strong>Note:</strong> {endpoint.example}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Pagination */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 mt-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Pagination</h2>
              <p className="text-gray-600 mb-4">
                All list endpoints support pagination with these query parameters:
              </p>
              <table className="w-full text-sm mb-4">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-2 text-gray-600 font-medium">Parameter</th>
                    <th className="text-left py-2 text-gray-600 font-medium">Default</th>
                    <th className="text-left py-2 text-gray-600 font-medium">Max</th>
                    <th className="text-left py-2 text-gray-600 font-medium">Description</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  <tr>
                    <td className="py-2"><code className="bg-gray-100 px-1 rounded">page</code></td>
                    <td className="py-2">1</td>
                    <td className="py-2">-</td>
                    <td className="py-2">Page number</td>
                  </tr>
                  <tr>
                    <td className="py-2"><code className="bg-gray-100 px-1 rounded">limit</code></td>
                    <td className="py-2">20</td>
                    <td className="py-2">100</td>
                    <td className="py-2">Items per page</td>
                  </tr>
                </tbody>
              </table>
              <p className="text-gray-500 text-sm mb-3">
                Responses include a <code className="bg-gray-100 px-1 rounded">pagination</code> object:
              </p>
              <pre className="bg-gray-900 text-gray-100 rounded-lg p-4 overflow-x-auto text-sm">{`{
  "pagination": {
    "page": 1,
    "limit": 20,
    "total": 57,
    "totalPages": 3
  }
}`}</pre>
            </div>

            {/* Webhooks Detail */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 mt-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Webhooks</h2>
              <p className="text-gray-600 mb-4">
                Configure webhooks to receive real-time notifications when forms receive submissions.
                Set up webhooks in the form&apos;s <strong>Settings &rarr; Submit Actions</strong> by adding a webhook action.
              </p>

              <div className="space-y-6">
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Webhook Configuration</h3>
                  <p className="text-gray-600 text-sm mb-2">Add a webhook action in your form schema&apos;s submitActions:</p>
                  <pre className="bg-gray-900 text-gray-100 rounded-lg p-4 overflow-x-auto text-sm">{`{
  "type": "webhook",
  "url": "https://your-server.com/xtform-webhook",
  "method": "POST",
  "headers": {
    "X-Custom-Header": "your-value"
  }
}`}</pre>
                </div>

                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Webhook Payload</h3>
                  <p className="text-gray-600 text-sm mb-2">When a submission is received, xtFORM sends a POST to your URL:</p>
                  <pre className="bg-gray-900 text-gray-100 rounded-lg p-4 overflow-x-auto text-sm">{`{
  "formId": "a1b2c3d4-e5f6-7890-abcd-ef1234567890",
  "submissionId": "sub-9876-5432-wxyz",
  "data": {
    "full_name": "Jane Smith",
    "email": "jane@example.com",
    "message": "Hello!"
  },
  "metadata": {
    "ip": "203.0.113.42",
    "userAgent": "Mozilla/5.0...",
    "referrer": "https://example.com",
    "submittedAt": "2026-02-01T15:00:00Z"
  }
}`}</pre>
                </div>

                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Signature Verification</h3>
                  <p className="text-gray-600 text-sm mb-2">
                    For signed webhooks, verify the <code className="bg-gray-100 px-1 rounded">X-Webhook-Signature</code> header using HMAC-SHA256:
                  </p>
                  <pre className="bg-gray-900 text-gray-100 rounded-lg p-4 overflow-x-auto text-sm">{`// Node.js
const crypto = require('crypto');

function verifyWebhookSignature(payload, signature, secret) {
  const expected = crypto
    .createHmac('sha256', secret)
    .update(JSON.stringify(payload))
    .digest('hex');
  return crypto.timingSafeEqual(
    Buffer.from(signature),
    Buffer.from(expected)
  );
}

// PHP
function verifySignature($payload, $signature, $secret) {
    $expected = hash_hmac('sha256', json_encode($payload), $secret);
    return hash_equals($expected, $signature);
}

// Python
import hmac, hashlib, json

def verify_signature(payload, signature, secret):
    expected = hmac.new(
        secret.encode(),
        json.dumps(payload).encode(),
        hashlib.sha256
    ).hexdigest()
    return hmac.compare_digest(expected, signature)`}</pre>
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <h4 className="font-semibold text-amber-900 mb-2">Retry Behavior</h4>
                  <ul className="text-amber-800 text-sm space-y-1 list-disc list-inside">
                    <li>Failed deliveries (non-2xx responses) are retried up to 3 times</li>
                    <li>Retry delay: 60 seconds between attempts</li>
                    <li>After 3 consecutive failures, the webhook is marked as failing</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Embedding Forms */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 mt-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Embedding Forms</h2>
              <p className="text-gray-600 mb-4">
                Embed xtFORM forms on any website using iframes or build a custom renderer with the public API.
              </p>

              <div className="space-y-6">
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Basic iframe Embed</h3>
                  <pre className="bg-gray-900 text-gray-100 rounded-lg p-4 overflow-x-auto text-sm">{`<iframe
  src="https://xt4m.com/f/your-form-slug"
  width="100%"
  height="600"
  frameborder="0"
  style="border: none; max-width: 700px;"
></iframe>`}</pre>
                </div>

                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Auto-resizing iframe</h3>
                  <pre className="bg-gray-900 text-gray-100 rounded-lg p-4 overflow-x-auto text-sm">{`<iframe id="xtform-frame"
  src="https://xt4m.com/f/your-form-slug"
  width="100%" frameborder="0" style="border: none;">
</iframe>

<script>
window.addEventListener('message', function(e) {
  if (e.origin !== 'https://xt4m.com') return;
  if (e.data.type === 'xtform-resize') {
    document.getElementById('xtform-frame')
      .style.height = e.data.height + 'px';
  }
  if (e.data.type === 'xtform-submitted') {
    console.log('Submitted:', e.data.submissionId);
  }
});
</script>`}</pre>
                </div>

                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Pre-fill Fields via URL</h3>
                  <p className="text-gray-600 text-sm mb-2">
                    Append query parameters matching field <code className="bg-gray-100 px-1 rounded">name</code> values:
                  </p>
                  <pre className="bg-gray-900 text-gray-100 rounded-lg p-4 overflow-x-auto text-sm">{`https://xt4m.com/f/your-form-slug?name=John&email=john@example.com`}</pre>
                </div>

                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">URL Patterns</h3>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-2 text-gray-600 font-medium">Type</th>
                        <th className="text-left py-2 text-gray-600 font-medium">Public URL</th>
                        <th className="text-left py-2 text-gray-600 font-medium">API Endpoint</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      <tr>
                        <td className="py-2">Form</td>
                        <td className="py-2"><code className="bg-gray-100 px-1 rounded">xt4m.com/f/:slug</code></td>
                        <td className="py-2"><code className="bg-gray-100 px-1 rounded">GET /api/f/:slug</code></td>
                      </tr>
                      <tr>
                        <td className="py-2">Poll</td>
                        <td className="py-2"><code className="bg-gray-100 px-1 rounded">xt4m.com/p/:slug</code></td>
                        <td className="py-2"><code className="bg-gray-100 px-1 rounded">GET /api/p/:slug</code></td>
                      </tr>
                      <tr>
                        <td className="py-2">Quiz</td>
                        <td className="py-2"><code className="bg-gray-100 px-1 rounded">xt4m.com/q/:slug</code></td>
                        <td className="py-2"><code className="bg-gray-100 px-1 rounded">GET /api/q/:slug</code></td>
                      </tr>
                      <tr>
                        <td className="py-2">Live Quiz</td>
                        <td className="py-2"><code className="bg-gray-100 px-1 rounded">xt4m.com/l/:slug</code></td>
                        <td className="py-2"><code className="bg-gray-100 px-1 rounded">GET /api/l/:slug</code></td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-semibold text-blue-900 mb-1">WordPress Plugin</h4>
                  <p className="text-blue-800 text-sm">
                    For WordPress sites, use the official <strong>xtFORM Connector</strong> plugin for native rendering
                    (no iframe), shortcode support, Gutenberg block, and admin-level submission viewing.{' '}
                    <a href="https://xt4m.com/api/wordpress-plugin/download" className="underline font-medium">
                      Download the plugin
                    </a>
                  </p>
                </div>
              </div>
            </div>

            {/* Rate Limiting */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 mt-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Rate Limiting</h2>
              <p className="text-gray-600 mb-4">
                The API implements rate limiting to ensure fair usage and service stability. Limits are applied per API key.
              </p>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-2 text-gray-600 font-medium">Endpoint Type</th>
                    <th className="text-left py-2 text-gray-600 font-medium">Limit</th>
                    <th className="text-left py-2 text-gray-600 font-medium">Window</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  <tr>
                    <td className="py-2">API endpoints (with API key)</td>
                    <td className="py-2">1,000 requests</td>
                    <td className="py-2">1 hour</td>
                  </tr>
                  <tr>
                    <td className="py-2">Form submissions (public, no API key)</td>
                    <td className="py-2">10 requests per IP</td>
                    <td className="py-2">1 minute</td>
                  </tr>
                  <tr>
                    <td className="py-2">Analytics queries</td>
                    <td className="py-2">100 requests</td>
                    <td className="py-2">1 hour</td>
                  </tr>
                </tbody>
              </table>
              <p className="text-gray-500 text-sm mt-4">
                Rate limit headers are included in all responses:
                <code className="bg-gray-100 px-1 rounded ml-1">X-RateLimit-Limit</code>,
                <code className="bg-gray-100 px-1 rounded ml-1">X-RateLimit-Remaining</code>,
                <code className="bg-gray-100 px-1 rounded ml-1">X-RateLimit-Reset</code>
              </p>
              <p className="text-gray-500 text-sm mt-2">
                When the limit is exceeded, a <code className="bg-gray-100 px-1 rounded">429</code> response is returned
                with a <code className="bg-gray-100 px-1 rounded">retryAfter</code> field indicating seconds to wait.
              </p>
            </div>

            {/* Error Codes */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 mt-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Error Handling</h2>
              <p className="text-gray-600 mb-4">
                All errors return a JSON object with an <code className="bg-gray-100 px-1 rounded">error</code> field:
              </p>
              <pre className="bg-gray-900 text-gray-100 rounded-lg p-3 overflow-x-auto text-sm mb-4">{`{ "error": "Description of what went wrong" }`}</pre>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-2 text-gray-600 font-medium">Code</th>
                    <th className="text-left py-2 text-gray-600 font-medium">Description</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  <tr>
                    <td className="py-2"><code className="bg-gray-100 px-2 rounded">200</code></td>
                    <td className="py-2">Success</td>
                  </tr>
                  <tr>
                    <td className="py-2"><code className="bg-gray-100 px-2 rounded">201</code></td>
                    <td className="py-2">Created</td>
                  </tr>
                  <tr>
                    <td className="py-2"><code className="bg-gray-100 px-2 rounded">400</code></td>
                    <td className="py-2">Bad Request - Invalid parameters or request body</td>
                  </tr>
                  <tr>
                    <td className="py-2"><code className="bg-gray-100 px-2 rounded">401</code></td>
                    <td className="py-2">Unauthorized - Missing or invalid authentication</td>
                  </tr>
                  <tr>
                    <td className="py-2"><code className="bg-gray-100 px-2 rounded">403</code></td>
                    <td className="py-2">Forbidden - Insufficient permissions or scope missing</td>
                  </tr>
                  <tr>
                    <td className="py-2"><code className="bg-gray-100 px-2 rounded">404</code></td>
                    <td className="py-2">Not Found - Resource doesn&apos;t exist</td>
                  </tr>
                  <tr>
                    <td className="py-2"><code className="bg-gray-100 px-2 rounded">429</code></td>
                    <td className="py-2">Too Many Requests - Rate limit exceeded</td>
                  </tr>
                  <tr>
                    <td className="py-2"><code className="bg-gray-100 px-2 rounded">500</code></td>
                    <td className="py-2">Internal Server Error - Something went wrong</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Code Examples */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 mt-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Code Examples</h2>

              <div className="space-y-6">
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">cURL</h3>
                  <pre className="bg-gray-900 text-gray-100 rounded-lg p-4 overflow-x-auto text-sm">{`# List published forms
curl -H "Authorization: Bearer fk_your_key" \\
  "https://xt4m.com/api/forms?status=published"

# Create a form
curl -X POST -H "Authorization: Bearer fk_your_key" \\
  -H "Content-Type: application/json" \\
  -d '{"name": "Contact Form"}' \\
  "https://xt4m.com/api/forms"

# Publish a form
curl -X POST -H "Authorization: Bearer fk_your_key" \\
  "https://xt4m.com/api/forms/FORM_ID/publish"

# Submit data (no auth needed for published forms)
curl -X POST -H "Content-Type: application/json" \\
  -d '{"data": {"name": "Alice", "email": "alice@example.com"}}' \\
  "https://xt4m.com/api/forms/FORM_ID/submissions"

# List submissions
curl -H "Authorization: Bearer fk_your_key" \\
  "https://xt4m.com/api/forms/FORM_ID/submissions?limit=50"

# Export as CSV
curl -H "Authorization: Bearer fk_your_key" -o submissions.csv \\
  "https://xt4m.com/api/forms/FORM_ID/submissions/export?format=csv"`}</pre>
                </div>

                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Node.js / JavaScript</h3>
                  <pre className="bg-gray-900 text-gray-100 rounded-lg p-4 overflow-x-auto text-sm">{`class XtFormClient {
  constructor(apiKey, baseUrl = 'https://xt4m.com') {
    this.apiKey = apiKey;
    this.baseUrl = baseUrl;
  }

  async request(method, endpoint, data) {
    const options = {
      method,
      headers: {
        'Authorization': \`Bearer \${this.apiKey}\`,
        'Content-Type': 'application/json',
      },
    };
    if (data && ['POST', 'PUT'].includes(method)) {
      options.body = JSON.stringify(data);
    }
    const res = await fetch(\`\${this.baseUrl}\${endpoint}\`, options);
    if (!res.ok) throw new Error((await res.json()).error);
    return res.json();
  }

  listForms(params = {}) {
    const q = new URLSearchParams(params).toString();
    return this.request('GET', \`/api/forms\${q ? '?' + q : ''}\`);
  }
  getForm(id) { return this.request('GET', \`/api/forms/\${id}\`); }
  createForm(name) { return this.request('POST', '/api/forms', { name }); }
  publishForm(id) { return this.request('POST', \`/api/forms/\${id}/publish\`); }
  getSubmissions(formId, params = {}) {
    const q = new URLSearchParams(params).toString();
    return this.request('GET', \`/api/forms/\${formId}/submissions\${q ? '?' + q : ''}\`);
  }
  createSubmission(formId, data) {
    return this.request('POST', \`/api/forms/\${formId}/submissions\`, { data });
  }
  exportSubmissions(formId, format = 'json') {
    return this.request('GET', \`/api/forms/\${formId}/submissions/export?format=\${format}\`);
  }
}

const client = new XtFormClient('fk_your_api_key');
const { forms } = await client.listForms({ status: 'published' });
const { submissions } = await client.getSubmissions(forms[0].id);`}</pre>
                </div>

                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Python</h3>
                  <pre className="bg-gray-900 text-gray-100 rounded-lg p-4 overflow-x-auto text-sm">{`import requests

class XtFormClient:
    def __init__(self, api_key, base_url="https://xt4m.com"):
        self.session = requests.Session()
        self.session.headers.update({
            "Authorization": f"Bearer {api_key}",
            "Content-Type": "application/json",
        })
        self.base_url = base_url

    def _req(self, method, endpoint, **kwargs):
        r = self.session.request(method, f"{self.base_url}{endpoint}", **kwargs)
        r.raise_for_status()
        return r.json()

    def list_forms(self, **params):
        return self._req("GET", "/api/forms", params=params)

    def get_form(self, form_id):
        return self._req("GET", f"/api/forms/{form_id}")

    def create_form(self, name):
        return self._req("POST", "/api/forms", json={"name": name})

    def publish_form(self, form_id):
        return self._req("POST", f"/api/forms/{form_id}/publish")

    def get_submissions(self, form_id, **params):
        return self._req("GET", f"/api/forms/{form_id}/submissions", params=params)

    def create_submission(self, form_id, data):
        return self._req("POST", f"/api/forms/{form_id}/submissions", json={"data": data})

    def export_submissions(self, form_id, format="json"):
        return self._req("GET", f"/api/forms/{form_id}/submissions/export",
                         params={"format": format})

client = XtFormClient("fk_your_api_key")
result = client.list_forms(status="published")
for form in result["forms"]:
    print(f"{form['name']} ({form['slug']})")`}</pre>
                </div>
              </div>
            </div>

            {/* CTA */}
            <div className="bg-gradient-to-r from-blue-600 to-indigo-700 rounded-xl p-8 mt-8 text-white">
              <h2 className="text-xl font-bold mb-4">Need Help?</h2>
              <p className="text-blue-100 mb-6">
                Have questions about the API or need assistance with integration?
                Create a free account to get started, or reach out through your dashboard.
              </p>
              <div className="flex gap-4">
                <Link
                  to="/register"
                  className="px-6 py-3 bg-white text-blue-600 rounded-lg font-semibold hover:bg-blue-50 transition-colors"
                >
                  Get Started Free
                </Link>
              </div>
            </div>
          </main>
        </div>
      </div>
      <PublicFooter />
    </div>
  );
}
